import React from "react";
import { cn } from "../../utils/cn";

const Input = React.forwardRef(({
    className,
    type = "text",
    label,
    description,
    error,
    required = false,
    id,
    ...props
}, ref) => {
    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random()?.toString(36)?.substr(2, 9)}`;
    const errorId = error ? `${inputId}-error` : undefined;
    const descId = description && !error ? `${inputId}-desc` : undefined;

    // Base input classes - touch-optimized with min height for mobile
    const baseInputClasses = "flex min-h-[44px] w-full rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

    // Checkbox-specific styles
    if (type === "checkbox") {
        return (
            <input
                type="checkbox"
                className={cn(
                    "h-5 w-5 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}
                ref={ref}
                id={inputId}
                aria-invalid={!!error}
                aria-describedby={errorId || descId}
                {...props}
            />
        );
    }

    // Radio button-specific styles
    if (type === "radio") {
        return (
            <input
                type="radio"
                className={cn(
                    "h-5 w-5 rounded-full border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}
                ref={ref}
                id={inputId}
                aria-invalid={!!error}
                aria-describedby={errorId || descId}
                {...props}
            />
        );
    }

    // For regular inputs with wrapper structure
    return (
        <div className="space-y-1.5">
            {label && (
                <label
                    htmlFor={inputId}
                    className={cn(
                        "block text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 py-0.5",
                        error ? "text-destructive" : "text-foreground"
                    )}
                >
                    {label}
                    {required && <span className="text-destructive ml-1" aria-hidden="true">*</span>}
                    {required && <span className="sr-only"> (required)</span>}
                </label>
            )}

            <input
                type={type}
                className={cn(
                    baseInputClasses,
                    error && "border-destructive focus-visible:ring-destructive",
                    className
                )}
                ref={ref}
                id={inputId}
                aria-invalid={error ? "true" : undefined}
                aria-describedby={errorId || descId}
                aria-required={required || undefined}
                {...props}
            />

            {description && !error && (
                <p id={descId} className="text-sm text-muted-foreground">
                    {description}
                </p>
            )}

            {error && (
                <p id={errorId} className="text-sm text-destructive" role="alert" aria-live="polite">
                    {error}
                </p>
            )}
        </div>
    );
});

Input.displayName = "Input";

export default Input;